import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { BookingType, VenueConfig } from "../../../common/jsonRpcTypes";
import {
  fromDateString,
  getAvailableDateRange,
  getMonthAvailability,
  isDayAvailableForBookingType,
  toDateString,
  type AvailableDateRange,
  type DayAvailabilityResult,
  type MonthAvailabilityMap,
} from "../../utils/calendarAvailability";

export interface CalendarAvailabilityState {
  /** Min/max selectable dates based on advance booking constraints. */
  dateRange: AvailableDateRange;
  /** Availability map for the currently viewed month. */
  monthAvailability: MonthAvailabilityMap;
  /** The year/month currently being viewed in the calendar. */
  viewedYear: number;
  viewedMonth: number;
}

interface UseCalendarAvailabilityOptions {
  bookingType: BookingType | undefined;
  numPeople: number | undefined;
  selectedDate: string | undefined;
  source?: string;
  venueConfig?: VenueConfig | null;
  onDateInvalidated?: () => void;
}

function toViewableDate(dateStr: string | undefined): Date {
  if (!dateStr) {
    return new Date();
  }
  const date = fromDateString(dateStr);
  const isRealCalendarDate = !Number.isNaN(date.getTime()) && toDateString(date) === dateStr.split("T")[0];
  return isRealCalendarDate ? date : new Date();
}

/**
 * Hook that computes calendar day availability for the selected booking type.
 *
 * Recomputes when:
 * - Booking type changes
 * - Guest count changes
 * - Viewed month changes
 *
 * The viewed month follows the selected date whenever the date changes.
 *
 * If the currently selected date becomes unavailable after a recomputation
 * (e.g. booking type or guest count changed), calls `onDateInvalidated`.
 */
export function useCalendarAvailability({
  bookingType,
  numPeople,
  selectedDate,
  source,
  venueConfig,
  onDateInvalidated,
}: UseCalendarAvailabilityOptions) {
  const [viewedYear, setViewedYear] = useState(() => toViewableDate(selectedDate).getFullYear());
  const [viewedMonth, setViewedMonth] = useState(() => toViewableDate(selectedDate).getMonth());

  // Track previous booking type and num people to detect changes
  const prevBookingTypeIdRef = useRef<string | undefined>(bookingType?.id);
  const prevNumPeopleRef = useRef<number | undefined>(numPeople);

  // ── Date range from advance booking constraints ──
  const dateRange = useMemo(() => getAvailableDateRange(bookingType), [bookingType]);

  // ── Format min/max as YYYY-MM-DD for HTML date input ──
  const minDateStr = useMemo(() => toDateString(dateRange.minDate), [dateRange.minDate]);
  const maxDateStr = useMemo(() => toDateString(dateRange.maxDate), [dateRange.maxDate]);

  // ── Compute availability for the viewed month ──
  const monthAvailability = useMemo(
    () => getMonthAvailability(viewedYear, viewedMonth, bookingType, numPeople, source, venueConfig),
    [viewedYear, viewedMonth, bookingType, numPeople, source, venueConfig],
  );

  // ── Check a specific day's availability ──
  const checkDayAvailability = useCallback(
    (dateStr: string): DayAvailabilityResult => {
      return isDayAvailableForBookingType(dateStr, bookingType, numPeople, source, venueConfig);
    },
    [bookingType, numPeople, source, venueConfig],
  );

  // ── When booking type or guest count changes, check if selected date is still valid ──
  useEffect(() => {
    const bookingTypeChanged = prevBookingTypeIdRef.current !== bookingType?.id;
    const numPeopleChanged = prevNumPeopleRef.current !== numPeople;

    prevBookingTypeIdRef.current = bookingType?.id;
    prevNumPeopleRef.current = numPeople;

    if ((bookingTypeChanged || numPeopleChanged) && selectedDate && bookingType) {
      const result = isDayAvailableForBookingType(selectedDate, bookingType, numPeople, source, venueConfig);
      if (!result.available) {
        onDateInvalidated?.();
      }
    }
  }, [bookingType, numPeople, selectedDate, source, venueConfig, onDateInvalidated]);

  // ── Navigate to a different month ──
  const goToMonth = useCallback((year: number, month: number) => {
    setViewedYear(year);
    setViewedMonth(month);
  }, []);

  const goToDateMonth = useCallback((dateStr: string) => {
    const date = toViewableDate(dateStr);
    setViewedYear(date.getFullYear());
    setViewedMonth(date.getMonth());
  }, []);

  // ── Keep the calendar on the selected date's month when the date changes ──
  useEffect(() => {
    if (selectedDate) {
      goToDateMonth(selectedDate);
    }
  }, [selectedDate, goToDateMonth]);

  const goToNextMonth = useCallback(() => {
    setViewedMonth((prev) => {
      if (prev === 11) {
        setViewedYear((y) => y + 1);
        return 0;
      }
      return prev + 1;
    });
  }, []);

  const goToPreviousMonth = useCallback(() => {
    setViewedMonth((prev) => {
      if (prev === 0) {
        setViewedYear((y) => y - 1);
        return 11;
      }
      return prev - 1;
    });
  }, []);

  return {
    dateRange,
    minDateStr,
    maxDateStr,
    monthAvailability,
    viewedYear,
    viewedMonth,
    checkDayAvailability,
    goToMonth,
    goToDateMonth,
    goToNextMonth,
    goToPreviousMonth,
  };
}
